import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Attributes,
  Experiment,
  useExperiment,
  useFeatureValue,
  useGrowthBook,
  WidenPrimitives,
} from '@growthbook/growthbook-react'
import { AppFeatures } from './app-features'
import { isValidVariationArray } from './server'
import { DeferredTrackingCalls } from './types'
import { getExperimentValueFromQuery } from './utils'

const reconcileExperimentValue = <T extends keyof AppFeatures>(
  key: T,
  exposedValue: WidenPrimitives<AppFeatures[T]>,
) => {
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const keyValue = urlParams?.get(key)
  const fromQuery =
    typeof keyValue === 'string' ? (getExperimentValueFromQuery(keyValue) as WidenPrimitives<AppFeatures[T]>) : null
  return fromQuery !== null ? fromQuery : exposedValue
}

export function useGBExperiment<T extends keyof AppFeatures>(key: T, defaultValue: AppFeatures[T]) {
  const growthbook = useGrowthBook()
  const exposedValue = useFeatureValue<AppFeatures[T]>(key, defaultValue)

  const value = useMemo(() => {
    return reconcileExperimentValue(key, exposedValue)
  }, [key, exposedValue])

  return [value, growthbook.ready] as const
}

export function useExperimentContext() {
  const growthbook = useGrowthBook()

  const resolvedSetAttributes = useCallback(
    (attributes: Attributes) => {
      growthbook.setAttributes({ ...growthbook.getAttributes(), ...attributes })
    },
    [growthbook],
  )

  const getFeatureValue = useCallback(
    <T extends keyof AppFeatures>(key: T, defaultValue: AppFeatures[T]) => {
      return reconcileExperimentValue(key, growthbook.getFeatureValue(key, defaultValue))
    },
    [growthbook],
  )

  const runInlineExperiment = useCallback(
    <T>(exp: Experiment<T>): T => {
      return growthbook.run(exp).value
    },
    [growthbook],
  )

  return {
    getUserAttributes: growthbook.getAttributes,
    setUserAttributes: resolvedSetAttributes,
    getFeatureValue,
    runInlineExperiment,
    ready: growthbook.ready,
  }
}

export function useInlineExperiment<T>(exp: Experiment<T>) {
  const { value } = useExperiment(exp)

  return value
}

type ContentfulVariation = {
  sys: { id: string }
  __typename?: string
  rolloutVariationId?: string | null
} | null

type ContentfulVariationReturn<T> = NonNullable<
  Exclude<T, { __typename: 'GrowthbookExperimentMappings' } | { __typename?: 'GrowthbookExperimentMappings' }>
>

export function useContentfulExperiment<T extends ContentfulVariation>(
  rawVariations: T[] | null | undefined,
): ContentfulVariationReturn<T> | null {
  const { runInlineExperiment } = useExperimentContext()

  const experimentMapping = useMemo(
    () => rawVariations?.find((v) => v?.__typename === 'GrowthbookExperimentMappings'),
    [rawVariations],
  )
  const variations = useMemo((): ContentfulVariationReturn<T>[] => {
    if (!rawVariations) return []
    return rawVariations.filter(
      (v): v is ContentfulVariationReturn<T> =>
        !!v && 'sys' in v && !!v.sys?.id && v.__typename !== 'GrowthbookExperimentMappings',
    )
  }, [rawVariations])

  const firstVariation = variations[0]
  const rolledOutVariation = variations.find((v) => v.sys.id === experimentMapping?.rolloutVariationId)

  const variationIds = useMemo(() => variations.map((v) => v.sys.id), [variations])

  // Only run experiment if we have a valid experiment mapping and at least 2 variations
  let experimentResult: string | null = null
  if (experimentMapping?.sys?.id && isValidVariationArray(variationIds)) {
    experimentResult = runInlineExperiment({
      key: experimentMapping.sys.id,
      variations: variationIds,
    })
  }

  return useMemo(() => {
    // If only one variation, return it directly
    if (variations.length === 1 && firstVariation) return firstVariation
    // If there's a rollout variation set, use it (experiment is "rolled out" to a winner)
    if (rolledOutVariation) return rolledOutVariation
    // If no valid experiment setup, return null
    if (!experimentMapping?.sys?.id || variations.length === 0 || !firstVariation) return null
    // If not enough variations for an experiment, return the first one
    if (variationIds.length < 2) return firstVariation
    // Return the variation selected by the experiment, or fallback to first
    return variations.find((v) => v.sys.id === experimentResult) ?? firstVariation
  }, [
    variations,
    firstVariation,
    rolledOutVariation,
    experimentMapping?.sys?.id,
    variationIds.length,
    experimentResult,
  ])
}

/**
 * Hook to fire deferred tracking calls from server-side experiments.
 *
 * Use this when an experiment variant was determined on the server but the user
 * may not actually see the variation (e.g., they choose PayPal instead of card checkout).
 *
 * The tracking calls are only fired once, when shouldFire becomes true.
 *
 * @param deferredCalls - Deferred tracking calls from runExperimentsOnServerWithDeferred
 * @param experimentKeys - Optional filter: only fire tracking for these experiment keys.
 *                         If not provided, fires all deferred calls.
 * @param shouldFire - Whether to fire the tracking (default: true). Set to false to delay firing.
 *
 * @example
 * // Fire all deferred experiments when component renders
 * useFireDeferredTracking(deferredTrackingCalls)
 *
 * @example
 * // Fire only specific experiment when component renders
 * useFireDeferredTracking(deferredTrackingCalls, ['checkout-page-subscribe-cta-text'])
 *
 * @example
 * // Fire specific experiment conditionally
 * useFireDeferredTracking(deferredTrackingCalls, ['my-experiment'], isVisible)
 */
export function useFireDeferredTracking(
  deferredCalls: DeferredTrackingCalls | undefined,
  experimentKeys?: readonly string[],
  shouldFire: boolean = true,
): void {
  const growthbook = useGrowthBook()
  // Tracks which experiments have been fired during this component mount.
  // This only prevents duplicate fires within the same session (e.g., effect re-runs).
  // Cross-visit deduplication is handled by GrowthBook/Segment on the analytics side.
  const firedKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!shouldFire || !deferredCalls?.length || !growthbook.ready) {
      return
    }

    // Filter calls to only include requested experiments (or all if no filter)
    const keysToFire = experimentKeys ? new Set(experimentKeys) : null
    const callsToFire = deferredCalls.filter((call) => {
      const key = call.experiment.key
      // Skip if already fired
      if (firedKeysRef.current.has(key)) return false
      // Include if no filter or if key matches filter
      return !keysToFire || keysToFire.has(key)
    })

    if (callsToFire.length === 0) return

    // Mark as fired before actually firing
    callsToFire.forEach((call) => firedKeysRef.current.add(call.experiment.key))

    growthbook.setDeferredTrackingCalls(callsToFire)
    growthbook.fireDeferredTrackingCalls()
  }, [growthbook, deferredCalls, experimentKeys, shouldFire])
}
